/**
 * The right end of the Activity card's first line: status at rest, Archive and
 * Settle on hover.
 *
 * Extracted from `ActivityThreadCard` rather than grown in place. It is the
 * piece of that card the settle bug actually lived in, and it reads as one
 * thing — a slot two states take turns occupying — so it is worth finding on
 * its own.
 *
 * At rest this shows state. On hover or keyboard focus it shows the available
 * row actions, with Archive immediately before Settle. The visible state owns
 * the slot's width and the hidden one is taken out of flow, so the project
 * label reclaims the space instead of overlapping an invisible control.
 *
 * **Why the slot declares a group of its own.** The keyboard trigger is
 * `group-has-[:focus-visible]/sidebar-status-slot`, scoped to this span, and
 * not the row-wide `group-focus-within/menu-sub-item` it used to be. The card
 * is `tabIndex={0}`, so *any* click on it — left or right — focuses it and
 * satisfies focus-within over the whole `<li>`; the controls then pinned
 * themselves over the status label and stayed pinned after the pointer left.
 * That is the "I right-click and then it shows" half of the reported bug.
 * Upstream keys it the same way, on a `group/sidebar-status-slot` of its own,
 * with a comment naming the same failure.
 *
 * **Whether Settle is offered is not this component's rule.** It comes from
 * `resolveThreadSettleAvailability`, shared with the context menu, because the
 * two disagreeing is the other half of the bug: this card once hid the control
 * on a running thread while the menu still offered the action.
 *
 * Takes named values rather than the card's whole render context: the slot
 * needs a thread, a status, and two handlers, and spelling that out is what
 * lets it be read — and tested — without the card around it.
 */

import { CircleCheckIcon, CircleDashedIcon } from "lucide-react";
import type { MouseEvent, PointerEvent } from "react";

import { cn } from "~/lib/utils";
import type { ThreadStatusPill } from "../Sidebar.logic";
import type { SidebarThreadSummary } from "~/types";
import { ActivityCardWorkingDuration } from "./ActivityCardWorkingDuration";
import {
  resolveActivityCardStatus,
  resolveActivityCardWorkingStartedAt,
} from "./activityThreadCard.logic";
import { SidebarThreadSettleControl } from "./SidebarThreadSettleControl";
import {
  SidebarThreadArchiveControl,
  type SidebarThreadArchiveControlProps,
} from "./SidebarThreadArchiveControl";
import { resolveThreadSettleAvailability } from "./threadSettleAvailability";

export interface ActivityCardStatusSlotProps {
  readonly thread: SidebarThreadSummary;
  readonly threadStatus: ThreadStatusPill | null;
  /** The thread the user is currently looking at — it keeps its label at full strength. */
  readonly isActive: boolean;
  /** The thread's age, shown when it has no status worth naming. */
  readonly timeLabel: string;
  readonly archiveControl: SidebarThreadArchiveControlProps | null;
  readonly onSettlePointerDown: (event: PointerEvent<HTMLButtonElement>) => void;
  readonly onSettle: (event: MouseEvent<HTMLButtonElement>) => void;
}

function ActivityCardStatusGlyph(props: { readonly glyph: "working" | "done" | null }) {
  if (props.glyph === "working")
    return <CircleDashedIcon aria-hidden className="size-[var(--acard)] shrink-0" />;
  if (props.glyph === "done")
    return <CircleCheckIcon aria-hidden className="size-[var(--acard)] shrink-0" />;
  return null;
}

export function ActivityCardStatusSlot(props: ActivityCardStatusSlotProps) {
  const status = resolveActivityCardStatus({
    threadStatus: props.threadStatus,
    isActive: props.isActive,
  });

  const canSettle =
    resolveThreadSettleAvailability({ archiveState: props.thread.archive?.state ?? null }) ===
    "settle";
  const isConfirmingArchive = props.archiveControl?.state === "confirming";
  const hasActions = props.archiveControl !== null || canSettle;
  const showSettle = isConfirmingArchive ? false : canSettle;

  return (
    <span className="group/sidebar-status-slot relative ml-auto flex h-[calc(var(--acard)*1.25)] min-w-[calc(var(--acard)*2)] shrink-0 items-stretch justify-end text-[length:calc(var(--acard)*0.75)] leading-[var(--acard)]">
      <span
        // The slot, not the text inside it: this is what the settle control
        // trades places with, so it is what a position assertion has to hold.
        data-testid={`thread-status-slot-${props.thread.id}`}
        className={cn(
          "flex items-center self-center justify-self-end tabular-nums text-secondary-label transition-opacity",
          hasActions
            ? "pointer-events-none group-hover/menu-sub-item:absolute group-hover/menu-sub-item:right-0 group-hover/menu-sub-item:opacity-0 group-has-[:focus-visible]/sidebar-status-slot:absolute group-has-[:focus-visible]/sidebar-status-slot:right-0 group-has-[:focus-visible]/sidebar-status-slot:opacity-0"
            : undefined,
        )}
      >
        {status ? (
          <span
            className={cn(
              "inline-flex items-center gap-[calc(var(--acard)*0.25)] font-medium",
              status.colorClass,
            )}
          >
            <ActivityCardStatusGlyph glyph={status.glyph} />
            {/* The label alone is the live region. A `role="status"` wrapped
                around the ticking duration would make a screen reader announce
                the card once a second, for as long as the thread runs. */}
            <span role="status">{status.label}</span>
            {/* Gated on the glyph rather than on the literal "Working" label,
                so Forge's own `Connecting` gets the counter too. Upstream has
                no equivalent state; connecting is time spent waiting exactly
                like working is, and a card that says `Connecting` with no
                elapsed time is the one that leaves you wondering whether it is
                wedged. */}
            {status.glyph === "working" ? (
              <span aria-hidden>
                <ActivityCardWorkingDuration
                  startedAt={resolveActivityCardWorkingStartedAt(props.thread)}
                />
              </span>
            ) : null}
          </span>
        ) : (
          props.timeLabel
        )}
      </span>
      {hasActions ? (
        <span className="pointer-events-none absolute inset-y-0 right-0 inline-flex shrink-0 items-center opacity-0 transition-opacity duration-150 group-hover/menu-sub-item:pointer-events-auto group-hover/menu-sub-item:static group-hover/menu-sub-item:opacity-100 has-[:focus-visible]:pointer-events-auto has-[:focus-visible]:static has-[:focus-visible]:opacity-100">
          {props.archiveControl !== null ? (
            <SidebarThreadArchiveControl {...props.archiveControl} placement="cluster" />
          ) : null}
          {showSettle ? (
            <SidebarThreadSettleControl
              threadId={props.thread.id}
              title={props.thread.title}
              placement="cluster"
              onPointerDown={props.onSettlePointerDown}
              onSettle={props.onSettle}
            />
          ) : null}
        </span>
      ) : null}
    </span>
  );
}
